import {useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store.type';
import MobilePopup from '../../newPopup/mobilePopup/MobilePopup';
import Loader from '../../loader/Loader';
import * as css from './leadDetails.css';
import {XHR_LOADING_STATE} from '../../../assets/constants/constants';
import Btn from '../../button/btn/Btn';
import LeadItem from './campaignItem/leadItem';
import {TLivePromotionItem} from '../../../redux/publish/publish.type';
import SummaryStats from './SummaryStats/SummaryStats';
import CustomScrollbar from '../../customScrollbar/CustomScrollbar';
import {mobileMedia} from '../../../globalVariables/GlobalVariables';
import SearchLine from '../../search/SearchLine';
import getEventUrl from '../../../functions/getEventUrl';

const LeadDetails = ({isOpen, onClose, leadName, adaptedData=null, showClass}) => {
    const {t} = useTranslation('publish');
    const [searchTerm, setSearchTerm] = useState('');
    const contentRef = useRef<HTMLDivElement>(null);
    const {data, hubData, getDataXHR} = useSelector((state: RootState) => ({
        data: adaptedData || state.publish.lead[leadName],
        hubData: leadName === 'livePromotion' ? state.publish.lead.livePromotionHub : null,
        getDataXHR: state.publish.lead[`${leadName}XHR`]
    }));

    const event = useSelector((state: any) => state.event);
    const eventLink = getEventUrl(event.domain, event.landingPageUrl || event.name, event.type, null);

    const {filteredData, filteredHubData} = useMemo(() => {
        const filterItems = (items) => {
            if(!searchTerm.trim()) return items;
            return items?.filter(item => {
                const searchLower = searchTerm.toLowerCase();
                const nameMatch = item.name?.toLowerCase().includes(searchLower);
                
                // For livePromotion, also search in translated type and combined name
                if(leadName === 'livePromotion' && 'liveSourceType' in item) {
                    const liveItem = item as TLivePromotionItem;
                    const translatedType = t(liveItem.liveSourceType).toLowerCase();
                    const combinedName = liveItem.liveSourceType === 'homePage' 
                        ? translatedType
                        : `${translatedType}: ${liveItem.name}`.toLowerCase();
                    return nameMatch || translatedType.includes(searchLower) || combinedName.includes(searchLower);
                }
                
                return nameMatch;
            }) || [];
        };

        return {
            filteredData: filterItems(data),
            filteredHubData: leadName === 'livePromotion' ? filterItems(hubData) : []
        };
    }, [data, hubData, searchTerm, leadName, t]);

    const dataKeysList = leadName === 'livePromotion' ? [
        {key: 'sumTotalPrice', label: 'totalPrice', suffix: ' ₪'},
        {key: 'countOrders', label: 'transactions', suffix: ''},
        {key: 'amountTickets', label: 'tickets', suffix: ''},
        {key: 'totalVisits', label: 'ticliCount', suffix: ''},
        {key: 'uniqueUsers', label: 'ticliCountUnique', suffix: ''}
    ] : [
        {key: 'clicked', label: 'Entrances', suffix: ''},
        {key: 'purchased', label: 'transactions', suffix: ''},
        {key: 'tickets', label: 'tickets', suffix: ''},
        {key: 'revenue', label: 'sales', suffix: '₪'},
        {key: 'uniqueClicked', label: 'uniqueClicked', suffix: ''}
    ];
    const calculateSummary = (items) => {
        return items ? items.reduce((acc, item) => {
            dataKeysList.forEach(field => {
                if(field.key in item) {
                    acc[field.key] = (acc[field.key] || 0) + Number(item[field.key]) || 0;
                }
            });
            return acc;
        }, {} as any) : {};
    };

    const summaryData = calculateSummary(filteredData);
    const hubSummaryData = leadName === 'livePromotion' ? calculateSummary(filteredHubData) : {};
 
    if(!isOpen) return null;
    const popupProps = {
        cssStyle: 'height:75%; width:440px;',
        close: onClose,
        showClass: showClass,
        list: false,
        forwardRef: null,
        TopComponent: null,
    };

    if(getDataXHR === XHR_LOADING_STATE) {
        return (
            <MobilePopup
                {...popupProps}
                title={t(`${leadName}DetailsTitle`)} 
                dataCyName="lead-data-loading" >
                <Loader XHR={XHR_LOADING_STATE} height="200px">
                    <></>
                </Loader>
            </MobilePopup>
        );
    }

    const hasData = (data && data.length > 0) || (hubData && hubData.length > 0);
    if(!hasData) {
        return (
            <MobilePopup 
                {...popupProps}
                title={t(`${leadName}DetailsTitle`)} 
                dataCyName="lead-data-empty" >
                <css.error>{t('noDataAvailable')}</css.error>
            </MobilePopup>
        );
    }
    
    const cssStyle = `
    inset-inline-end: -20px;
    ${mobileMedia}{
        display: none
    }
`;
    return (
        <MobilePopup 
            {...popupProps}
            title={t(`${leadName}DetailsTitle`)} 
            dataCyName="lead-details" >
            <css.content>
                <SearchLine 
                    filter={searchTerm}
                    setFilter={setSearchTerm}
                    title={t(`search${leadName}`)}
                    action={() => {}}
                    customCss={{
                        container: css.searchContainer,
                        input: css.searchInput
                    }}
                />
                <CustomScrollbar contentRef={contentRef} cssStyle={cssStyle} />
                <css.scrollableContainer ref={contentRef}>
                    {leadName === 'livePromotion' ? (
                        <>
                            {/* Totals before list */}
                            <SummaryStats summaryKeysList={dataKeysList} summaryData={summaryData} t={t}/>
                            
                            {/* Other sources section */}
                            <css.leadsList>
                                {filteredData.map((item, index) => {
                                    let displayData = item;
                                    if('liveSourceType' in item) {
                                        const liveItem = item as TLivePromotionItem;
                                        const sourceTypeLabel = t(liveItem.liveSourceType);
                                        const displayName = liveItem.liveSourceType === 'homePage' 
                                            ? sourceTypeLabel
                                            : `${sourceTypeLabel}: ${liveItem.name}`;
                                        displayData = {...liveItem, name: displayName};
                                    }
                                    return <LeadItem key={`other-${index}`} dataKeys={dataKeysList} data={displayData} index={index} t={t} eventLink={eventLink}/>;
                                })}
                            </css.leadsList>
                            
                            {/* Separator */}
                            <css.separator />
                            
                            {/* Hub sources section */}
                            <css.sectionTitle>{t('hubPagesSection')}</css.sectionTitle>
                            
                            {/* Hub totals before hub list */}
                            <SummaryStats summaryKeysList={dataKeysList} summaryData={hubSummaryData} t={t}/>
                            
                            <css.leadsList>
                                {filteredHubData.map((item, index) => {
                                    let displayData = item;
                                    if('liveSourceType' in item) {
                                        const liveItem = item as TLivePromotionItem;
                                        const sourceTypeLabel = t(liveItem.liveSourceType);
                                        const displayName = liveItem.liveSourceType === 'homePage' 
                                            ? sourceTypeLabel
                                            : `${sourceTypeLabel}: ${liveItem.name}`;
                                        displayData = {...liveItem, name: displayName};
                                    }
                                    return <LeadItem key={`hub-${index}`} dataKeys={dataKeysList} data={displayData} index={index} t={t} eventLink={eventLink}/>;
                                })}
                            </css.leadsList>
                        </>
                    ) : (
                        <>
                            <css.leadsList>
                                {filteredData.map((item, index) => {
                                    let displayData = item;
                                    if(leadName === 'livePromotion' && 'liveSourceType' in item) {
                                        const liveItem = item as TLivePromotionItem;
                                        const sourceTypeLabel = t(liveItem.liveSourceType);
                                        const displayName = liveItem.liveSourceType === 'homePage' 
                                            ? sourceTypeLabel
                                            : `${sourceTypeLabel}: ${liveItem.name}`;
                                        displayData = {...liveItem, name: displayName};
                                    }
                                    return <LeadItem key={index} dataKeys={dataKeysList} data={displayData} index={index} t={t} eventLink={eventLink}/>;
                                })}
                            </css.leadsList>
                            <SummaryStats summaryKeysList={dataKeysList} summaryData={summaryData} t={t}/>
                        </>
                    )}
                </css.scrollableContainer>
                <css.popupBottom>
                    <Btn className='white' onClick={onClose}>
                        {t('close')}
                    </Btn>
                </css.popupBottom>
            </css.content>
        </MobilePopup>
    );
};

export default LeadDetails;

